// One loader hook for the platform pages (chats, ontologies, connections, members, tokens, graph): runs `fn` on
// mount and when `key` changes, again on reload() and on changes of the given kinds (live/bus.ts: the kit's replica
// says which kinds the log changed; reads are views over it, so a reload costs no request). No polling. Keeps the
// last data while reloading; errors show next to it.
// Shared by key: every hook on screen with the same key (several pages, one live event waking them all) rides one
// request started within FRESH_MS and after the last live event; only an explicit reload() (after a write) always asks
// again. A load started before an event read the replica without it: never shared past it (a write's own event may
// come after its reply, Cloudflare's publish runs after the response: the reload after the write rides no stale read).
import { useEffect, useRef, useState } from "react";
import { onLive } from "@/live/bus";
import { errorText } from "@/store/auth";

/** `loading`: nothing to show yet for this key (first load, key change) \u2014 drives `aria-busy` (the CSS loader);
 *  background reloads and polls keep `busy` only, so they don't flash it. */
export type Loaded<T> = { data: T | null; error: string | null; busy: boolean; loading: boolean; reload: () => void };

const FRESH_MS = 2000;
/** Live events so far (any kind): a shared answer from an earlier epoch is older than the replica. Registered before any
 *  hook's listener, so it counts an event before the hooks it wakes load. */
let epoch = 0;
onLive(() => void epoch++);
const shared = new Map<string, { p: Promise<unknown>; at: number; epoch: number }>();
function fetchShared<T>(key: string, fn: () => Promise<T>, force: boolean): Promise<T> {
	const hit = shared.get(key);
	if (hit && !force && hit.epoch === epoch && Date.now() - hit.at < FRESH_MS) return hit.p as Promise<T>;
	const p = fn();
	shared.set(key, { p, at: Date.now(), epoch });
	p.catch(() => shared.get(key)?.p === p && shared.delete(key));
	return p;
}
/** Forget the shared answers of keys starting with `prefix`: the next load asks again (a write whose live event may
 *  come within FRESH_MS of the last load). */
export const expire = (prefix: string) => {
	for (const k of shared.keys()) if (k.startsWith(prefix)) shared.delete(k);
};

export function useLoad<T>(fn: () => Promise<T>, key: string, opts: { kinds?: string[] } = {}): Loaded<T> {
	const [state, setState] = useState<{ key: string; data: T | null; error: string | null; busy: boolean }>({ key, data: null, error: null, busy: true });
	const [rev, setRev] = useState(0);
	const fnRef = useRef(fn);
	fnRef.current = fn;
	const force = useRef(false);
	// biome-ignore lint/correctness/useExhaustiveDependencies: keyed
	useEffect(() => {
		let live = true;
		setState((s) => ({ ...s, busy: true }));
		const f = force.current;
		force.current = false;
		fetchShared(key, () => fnRef.current(), f).then(
			(data) => live && setState({ key, data, error: null, busy: false }),
			(e) => live && setState((s) => ({ ...s, error: errorText(e), busy: false })),
		);
		return () => {
			live = false;
		};
	}, [key, rev]);
	const kinds = opts.kinds?.join(",") ?? "";
	// biome-ignore lint/correctness/useExhaustiveDependencies: keyed on the kinds list
	useEffect(() => {
		const want = kinds ? kinds.split(",") : [];
		return want.length ? onLive((kind) => want.some((k) => k === "*" || kind === k || kind.endsWith(`.${k}`)) && setRev((n) => n + 1)) : () => {};
	}, [kinds]);
	const data = state.key === key ? state.data : null;
	return { data, error: state.error, busy: state.busy, loading: data === null && !state.error,
		reload: () => {
			force.current = true;
			setRev((n) => n + 1);
		},
	};
}
